<!-- src/pages/ShoppingItemsView.vue -->
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_SHOPPING_ITEMS } from '../graphql/queries'
import { DELETE_SHOPPING_ITEM, UPDATE_SHOPPING_ITEM } from '../graphql/mutations'
import { showError, showSuccess } from '../utils/errorHandling'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import EditShoppingItemModal from '../components/shopping-item/EditShoppingItemModal.vue'

const router = useRouter()

// State
const searchQuery = ref('')
const statusFilter = ref('')
const sortBy = ref('created_at')
const sortOrder = ref('desc')
const showDeleteDialog = ref(false)
const showEditModal = ref(false)
const selectedItem = ref<any>(null)

// GraphQL queries
const { result: shoppingItemsResult, loading: loadingItems, refetch: refetchItems } = useQuery(GET_SHOPPING_ITEMS)

// Mutations
const { mutate: deleteShoppingItemMutation, loading: deletingItem } = useMutation(DELETE_SHOPPING_ITEM)
const { mutate: updateShoppingItemMutation, loading: updatingItem } = useMutation(UPDATE_SHOPPING_ITEM)

// Computed properties
const shoppingItems = computed(() => shoppingItemsResult.value?.shoppingItems || [])

const filteredAndSortedItems = computed(() => {
  let filtered = shoppingItems.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((item: any) =>
      item.name?.toLowerCase().includes(query) ||
      item.list_name?.toLowerCase().includes(query) ||
      item.type?.toLowerCase().includes(query)
    )
  }

  // Status filter
  if (statusFilter.value) {
    filtered = filtered.filter((item: any) => {
      if (statusFilter.value === 'completed') return item.is_bought
      if (statusFilter.value === 'pending') return !item.is_bought
      return true
    })
  }

  // Sort
  return [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortBy.value]
    let bVal = b[sortBy.value]

    if (sortBy.value === 'name') {
      aVal = a.name || ''
      bVal = b.name || ''
    }

    if (sortBy.value === 'shopping_list') {
      aVal = a.list_name || ''
      bVal = b.list_name || ''
    }

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }

    if (sortOrder.value === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })
})

const statusOptions = [
  { value: '', label: 'All Items' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' }
]

const sortOptions = [
  { value: 'created_at', label: 'Date Created' },
  { value: 'name', label: 'Name' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'shopping_list', label: 'Shopping List' }
]

// Methods
const editItem = (item: any) => {
  selectedItem.value = item
  showEditModal.value = true
}

const deleteItem = (item: any) => {
  selectedItem.value = item
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!selectedItem.value) return

  try {
    await deleteShoppingItemMutation({ id: selectedItem.value.id })
    showDeleteDialog.value = false
    selectedItem.value = null
    showSuccess('Shopping item deleted successfully!')
    refetchItems()
  } catch (error) {
    showError(error, 'delete shopping item')
  }
}

const cancelDelete = () => {
  showDeleteDialog.value = false
  selectedItem.value = null
}

const onItemUpdated = () => {
  showEditModal.value = false
  selectedItem.value = null
  refetchItems()
}

const toggleItemStatus = async (item: any) => {
  try {
    await updateShoppingItemMutation({
      id: item.id,
      list_id: item.list_id,
      name: item.name,
      ingredient_id: item.ingredient_id,
      quantity: item.quantity,
      unit: item.unit,
      price: item.price,
      type: item.type,
      is_bought: !item.is_bought,
    })
    showSuccess(`Item marked as ${!item.is_bought ? 'completed' : 'pending'}!`)
    refetchItems()
  } catch (error) {
    showError(error, 'update item status')
  }
}

const clearFilters = () => {
  searchQuery.value = ''
  statusFilter.value = ''
  sortBy.value = 'created_at'
  sortOrder.value = 'desc'
}
// Group items by shopping list for better organization
const groupedItemsByShoppingList = computed(() => {
  return filteredAndSortedItems.value.reduce((groups: any, item: any) => {
    // Use list_id and list_name from the shopping item data
    const listId = item.list_id || 'unknown'
    const listName = item.list_name || 'Unknown List'

    if (!groups[listId]) {
      groups[listId] = {
        name: listName,
        items: [],
        completed: 0,
        total: 0,
        totalPrice: 0,
        completedPrice: 0
      }
    }
    groups[listId].items.push(item)
    groups[listId].total++
    groups[listId].totalPrice += item.price || 0

    if (item.is_bought) {
      groups[listId].completed++
      groups[listId].completedPrice += item.price || 0
    }
    return groups
  }, {})
})

// Utility function to format item types as CamelCase with spaces
const formatItemType = (type: string) => {
  if (!type) return ''
  // Convert camelCase to Title Case with spaces
  return type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
}

// Get progress color based on completion percentage
const getProgressColor = (completed: number, total: number) => {
  const percentage = (completed / total) * 100
  if (percentage === 100) return 'bg-green-500'
  if (percentage >= 75) return 'bg-primary-500'
  if (percentage >= 50) return 'bg-accent-500'
  if (percentage >= 25) return 'bg-secondary-500'
  return 'bg-gray-400'
}
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Shopping Items</h1>
        <p class="text-gray-600 mt-1">Track progress and manage items across all your shopping lists</p>
      </div>
      <div class="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
        {{ filteredAndSortedItems.length }} item{{ filteredAndSortedItems.length !== 1 ? 's' : '' }}
      </div>
    </div>

    <!-- Filters and Sort -->
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Search -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <input v-model="searchQuery" type="text" placeholder="Search items, lists, or notes..."
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors" />
        </div>

        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select v-model="statusFilter"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors">
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Sort By -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <select v-model="sortBy"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors">
            <option v-for="option in sortOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Sort Order & Clear -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Sort Order</label>
          <div class="flex gap-2">
            <select v-model="sortOrder"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
            <button @click="clearFilters"
              class="px-3 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors focus:ring-2 focus:ring-secondary-100">
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loadingItems" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
      <p class="mt-4 text-gray-500">Loading shopping items...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredAndSortedItems.length === 0" class="text-center py-12">
      <div class="text-6xl mb-4">📝</div>
      <h3 class="text-xl font-semibold text-gray-700 mb-2">No shopping items found</h3>
      <p class="text-gray-500 mb-4">
        {{ shoppingItems.length === 0
          ? 'Items will appear here when you add them to shopping lists'
          : 'Try adjusting your search or filters'
        }}
      </p>
      <div class="space-x-2">
        <button v-if="searchQuery || statusFilter" @click="clearFilters"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors focus:ring-2 focus:ring-primary-100">
          Clear Filters
        </button>
        <button @click="router.push('/shopping-lists')"
          class="px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors focus:ring-2 focus:ring-accent-100">
          Go to Shopping Lists
        </button>
      </div>
    </div>

    <!-- Items grouped by category -->
    <div v-else class="space-y-8">
      <!-- Quick Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div class="flex flex-wrap gap-3">
          <button @click="router.push('/shopping-lists?action=create')"
            class="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6">
              </path>
            </svg>
            Create Shopping List
          </button>
          <button @click="router.push('/recipes')"
            class="inline-flex items-center px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253">
              </path>
            </svg>
            Browse Recipes
          </button>
          <button @click="router.push('/ingredients')"
            class="inline-flex items-center px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors focus:ring-2 focus:ring-accent-500 focus:ring-offset-2">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-7H5m14 14H5"></path>
            </svg>
            Manage Ingredients
          </button>
        </div>
      </div>

      <!-- Shopping List grouped items -->
      <div v-for="(listData, listId) in groupedItemsByShoppingList" :key="listId"
        class="bg-white rounded-lg shadow-md overflow-hidden">
        <!-- Shopping List header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div class="flex items-center gap-4">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">{{ listData.name }}</h3>
                <div class="flex items-center gap-4 mt-1">
                  <span class="text-sm text-gray-600">
                    {{ listData.completed }}/{{ listData.total }} completed
                  </span>
                  <span v-if="listData.totalPrice > 0" class="text-sm text-gray-600">
                    €{{ listData.completedPrice.toFixed(2) }}/€{{ listData.totalPrice.toFixed(2) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Progress bar and actions -->
            <div class="flex items-center gap-4">
              <!-- Progress visualization -->
              <div class="flex items-center gap-3">
                <div class="w-40 bg-gray-200 rounded-full h-3">
                  <div class="h-3 rounded-full transition-all duration-500"
                    :class="getProgressColor(listData.completed, listData.total)"
                    :style="{ width: `${(listData.completed / listData.total) * 100}%` }"></div>
                </div>
                <span class="text-sm font-semibold text-gray-700 min-w-[3rem] text-right">
                  {{ Math.round((listData.completed / listData.total) * 100) }}%
                </span>
              </div>

              <!-- Quick action to view shopping list -->
              <button @click="router.push(`/shopping-lists/${listId}`)"
                class="inline-flex items-center px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
                View List
              </button>
            </div>
          </div>
        </div>

        <!-- Shopping List items -->
        <div class="divide-y divide-gray-100">
          <div v-for="item in listData.items" :key="item.id"
            class="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
            <!-- Large checkbox -->
            <button @click="toggleItemStatus(item)"
              class="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200"
              :class="item.is_bought
                ? 'bg-green-500 border-green-500 text-white'
                : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'" :disabled="updatingItem"
              :aria-label="item.is_bought ? 'Mark as pending' : 'Mark as completed'">
              <svg v-if="item.is_bought" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </button>

            <!-- Item content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <!-- Item name with strikethrough for completed -->
                  <h4 class="text-base font-medium transition-all duration-200"
                    :class="item.is_bought ? 'line-through text-gray-500' : 'text-gray-900'">
                    {{ item.name || 'Unnamed Item' }}
                  </h4>

                  <!-- Quantity, price, notes, and category -->
                  <div class="flex flex-wrap items-center gap-2 text-sm mt-1 transition-all duration-200"
                    :class="item.is_bought ? 'text-gray-400' : 'text-gray-600'">
                    <span v-if="item.quantity" class="inline-flex items-center">
                      {{ item.quantity }} {{ item.unit || '' }}
                    </span>
                    <span v-if="item.price"
                      class="inline-flex items-center px-2 py-1 bg-accent-50 text-accent-700 rounded-full text-xs font-medium">
                      €{{ item.price.toFixed(2) }}
                    </span>
                    <span v-if="item.type"
                      class="inline-flex items-center px-2 py-1 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium">
                      {{ formatItemType(item.type) }}
                    </span>
                    <span v-if="item.notes" class="text-gray-500 italic">
                      {{ item.notes }}
                    </span>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="flex-shrink-0 flex items-center gap-1 ml-4">
                  <button @click="editItem(item)"
                    class="p-2 text-gray-400 hover:text-primary-600 rounded transition-colors" aria-label="Edit item">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                      </path>
                    </svg>
                  </button>

                  <button @click="deleteItem(item)"
                    class="p-2 text-gray-400 hover:text-red-500 rounded transition-colors" aria-label="Delete item">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                      </path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div v-if="shoppingItems.length > 0" class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <div class="text-3xl font-bold text-primary-600 mb-2">
          {{ shoppingItems.length }}
        </div>
        <div class="text-gray-600">Total Items</div>
      </div>

      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <div class="text-3xl font-bold text-accent-600 mb-2">
          {{shoppingItems.filter((item: any) => item.is_bought).length}}
        </div>
        <div class="text-gray-600">Completed</div>
      </div>

      <div class="bg-white rounded-lg shadow-md p-6 text-center">
        <div class="text-3xl font-bold text-secondary-600 mb-2">
          {{shoppingItems.filter((item: any) => !item.is_bought).length}}
        </div>
        <div class="text-gray-600">Pending</div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog :show="showDeleteDialog" title="Delete Shopping Item"
      :message="selectedItem ? `Are you sure you want to delete this shopping item? This action cannot be undone.` : ''"
      confirm-text="Delete" :loading="deletingItem" @confirm="confirmDelete" @cancel="cancelDelete" />

    <!-- Edit Shopping Item Modal -->
    <EditShoppingItemModal :show="showEditModal" :shopping-item="selectedItem" @close="showEditModal = false"
      @updated="onItemUpdated" />
  </div>
</template>
